import { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { HomeRedirect } from './HomeRedirect';

// ── Public pages ──────────────────────────────────────────────────────────────
const LoginPage    = lazy(() => import('@/pages/LoginPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const ForbiddenPage = lazy(() => import('@/pages/ForbiddenPage'));
const SsoCallbackPage = lazy(() => import('@/pages/SsoCallbackPage'));

// Department select
const DepartmentSelectPage = lazy(() => import('@/pages/DepartmentSelectPage'));

// Department instance
const DeptLayout       = lazy(() => import('@/features/dept/DeptLayout'));
const DeptDashboardPage = lazy(() => import('@/features/dept/DeptDashboardPage'));
const DeptTicketsPage  = lazy(() => import('@/features/dept/DeptTicketsPage'));
const DeptKnowledgePage = lazy(() => import('@/features/dept/DeptKnowledgePage'));
const DeptReportsPage  = lazy(() => import('@/features/dept/DeptReportsPage'));

// ── Protected pages (code-split per module) ───────────────────────────────────
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));

// Tickets
const TicketsPage      = lazy(() => import('@/features/tickets/pages/TicketsPage'));
const TicketDetailPage = lazy(() => import('@/features/tickets/pages/TicketDetailPage'));

// Assets
const AssetsPage            = lazy(() => import('@/features/assets/pages/AssetsPage'));
const AssetDetailPage       = lazy(() => import('@/features/assets/pages/AssetDetailPage'));
const AssetDashboardPage    = lazy(() => import('@/features/assets/pages/AssetDashboardPage'));
const AssetMaintenancePage  = lazy(() => import('@/features/assets/pages/AssetMaintenancePage'));

// Payments
const PaymentsPage = lazy(() => import('@/features/payments/pages/PaymentsPage'));
const PaymentDetailPage  = lazy(() => import('@/features/payments/pages/PaymentDetailPage'));
const ContractDetailPage = lazy(() => import('@/features/payments/pages/ContractDetailPage'));

// Licenses
const LicensesPage = lazy(() => import('@/features/licenses/pages/LicensesPage'));
const LicenseDetailPage = lazy(() => import('@/features/licenses/pages/LicenseDetailPage'));

// Users
const UsersPage = lazy(() => import('@/features/users/pages/UsersPage'));

// Roles
const RolesPage = lazy(() => import('@/features/roles/pages/RolesPage'));

// Templates
const TemplatesPage        = lazy(() => import('@/pages/TemplatesPage'));
const TemplateBuilderPage  = lazy(() => import('@/pages/TemplateBuilderPage'));

// Asset Template Builder
const AssetTemplateBuilderPage = lazy(() => import('@/pages/AssetTemplateBuilderPage'));

// Settings
const SettingsPage       = lazy(() => import('@/pages/SettingsPage'));
const EmailSettingsPage  = lazy(() => import('@/pages/EmailSettingsPage'));
const TicketCategoriesPage = lazy(() => import('@/pages/settings/TicketCategoriesPage'));
const CannedResponsesPage  = lazy(() => import('@/pages/settings/CannedResponsesPage'));
const SitesPage            = lazy(() => import('@/pages/settings/SitesPage'));
const AssetCategoriesPage  = lazy(() => import('@/pages/settings/AssetCategoriesPage'));
const PlatformAnalysisPage = lazy(() => import('@/pages/PlatformAnalysisPage'));

// Profile
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));

// ─────────────────────────────────────────────────────────────────────────────

export function AppRouter() {
  return (
    <Routes>
      {/* ── Public ── */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/sso-callback" element={<SsoCallbackPage />} />
      <Route path="/403"   element={<ForbiddenPage />} />

      {/* ── Authenticated ── */}
      <Route element={<ProtectedRoute />}>

        {/* Department selection page (no MainLayout) */}
        <Route path="/departments" element={<DepartmentSelectPage />} />

        <Route element={<MainLayout />}>

          <Route path="/" element={<HomeRedirect />} />

          {/* Dashboard — requires analytics:read (END_USER excluded) */}
          <Route element={<ProtectedRoute permission="analytics:read" />}>
            <Route path="/dashboard" element={<DashboardPage />} />
          </Route>

          {/* Tickets */}
          <Route element={<ProtectedRoute permission="tickets:read" />}>
            <Route path="/tickets"     element={<TicketsPage />} />
            <Route path="/tickets/:id" element={<TicketDetailPage />} />
          </Route>

          {/* Asset analytics dashboard — requires analytics:read (admin/tech view) */}
          <Route element={<ProtectedRoute permission="analytics:read" />}>
            <Route path="/assets/dashboard" element={<AssetDashboardPage />} />
          </Route>

          {/* Asset list & detail — requires assets:read (end users can see own assets) */}
          <Route element={<ProtectedRoute permission="assets:read" />}>
            <Route path="/assets"     element={<AssetsPage />} />
            <Route path="/assets/maintenance" element={<AssetMaintenancePage />} />
            <Route path="/assets/:id" element={<AssetDetailPage />} />
          </Route>

          {/* Payments */}
          <Route element={<ProtectedRoute permission="payments:read" />}>
            <Route path="/payments" element={<PaymentsPage />} />
            <Route path="/payments/contracts/:id" element={<ContractDetailPage />} />
            <Route path="/payments/:id" element={<PaymentDetailPage />} />
          </Route>

          {/* Licenses */}
          <Route element={<ProtectedRoute permission="licenses:read" />}>
            <Route path="/licenses" element={<LicensesPage />} />
            <Route path="/licenses/:id" element={<LicenseDetailPage />} />
          </Route>

          {/* Users */}
          <Route element={<ProtectedRoute permission="users:read" />}>
            <Route path="/users" element={<UsersPage />} />
          </Route>

          {/* Roles */}
          <Route element={<ProtectedRoute permission="roles:read" />}>
            <Route path="/roles" element={<RolesPage />} />
          </Route>

          {/* Templates */}
          <Route element={<ProtectedRoute permission="templates:read" />}>
            <Route path="/templates"          element={<TemplatesPage />} />
            <Route path="/templates/new"      element={<TemplateBuilderPage />} />
            <Route path="/templates/:id/edit" element={<TemplateBuilderPage />} />
          </Route>

          {/* Asset Template Builder (list is a tab inside /templates) */}
          <Route element={<ProtectedRoute permission="asset_templates:read" />}>
            <Route path="/asset-templates/new"      element={<AssetTemplateBuilderPage />} />
            <Route path="/asset-templates/:id/edit" element={<AssetTemplateBuilderPage />} />
          </Route>

          {/* Profile — accessible to all authenticated users */}
          <Route path="/profile" element={<ProfilePage />} />

          {/* Canned responses — technicians manage their own saved replies */}
          <Route element={<ProtectedRoute permission="tickets:update" />}>
            <Route path="/settings/canned-responses" element={<CannedResponsesPage />} />
          </Route>

          {/* Settings — requires settings:read (END_USER excluded) */}
          <Route element={<ProtectedRoute permission="settings:read" />}>
            <Route path="/settings"                    element={<SettingsPage />} />
            <Route path="/settings/email"               element={<EmailSettingsPage />} />
            <Route path="/settings/platform-report"     element={<PlatformAnalysisPage />} />
            <Route element={<ProtectedRoute permission="settings:update" />}>
              <Route path="/settings/ticket-categories" element={<TicketCategoriesPage />} />
              <Route path="/settings/sites" element={<SitesPage />} />
              <Route path="/settings/asset-categories" element={<AssetCategoriesPage />} />
            </Route>
          </Route>

        </Route>

        {/* ── Department instance routes ── */}
        <Route path="/:deptCode" element={<DeptLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<DeptDashboardPage />} />
          <Route path="tickets"   element={<DeptTicketsPage />} />
          <Route path="tickets/:id" element={<TicketDetailPage />} />
          <Route path="knowledge" element={<DeptKnowledgePage />} />
          <Route path="reports"   element={<DeptReportsPage />} />
        </Route>

      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
